import React, {
    forwardRef, useEffect, useImperativeHandle,
    useMemo, useRef, useState
} from "react";

type Props = {
    value?: string;
    values?: string[];
    stopEditing?: () => void;
    showAllOnEdit?: boolean;
    initialValue?: string;
};

const TextSuggestEditor = forwardRef<any, Props>((props, ref) => {
    const original = useRef<string>(props.value ?? "");
    const [val, setVal] = useState<string>(props.value ?? "");
    const inputRef = useRef<HTMLInputElement>(null);
    const listId = useMemo(
        () => `datalist-${Math.random().toString(36).slice(2)}`, []);

    const openSuggestions = () => {
        const el = inputRef.current;
        if (!el) return;

        if (props.showAllOnEdit !== false) {
            setVal("");
        }

        (el as any).showPicker?.();
    };

    useImperativeHandle(ref, () => ({
        getValue: () => (val.trim() === "" ? original.current : val),
        afterGuiAttached: () => {
            requestAnimationFrame(() => {
                inputRef.current?.focus();
                if (props.showAllOnEdit !== false) {
                    openSuggestions();
                } else {
                    inputRef.current?.select();
                }
            });
        },
    }));

    useEffect(() => {
        const t = setTimeout(() => {
            inputRef.current?.focus();
            if (props.showAllOnEdit !== false) {
                openSuggestions();
            } else {
                inputRef.current?.select();
            }
        }, 0);
        return () => clearTimeout(t);
    }, [props.showAllOnEdit]);

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation();

        if (!e.key || typeof e.key !== 'string') {
            return;
        }

        if (e.key === "Escape") {
            setVal(original.current);
            props.stopEditing?.();
        } else if (e.key === "Enter" || e.key === "Tab") {
            props.stopEditing?.();
        } else if (e.key === "ArrowDown" && props.showAllOnEdit !== false) {
            e.preventDefault();
            openSuggestions();
        }
    };

    const stopKeyEvent = (e: any) => {
        e.stopPropagation();
        if (e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === 'function') {
            e.nativeEvent.stopImmediatePropagation();
        }
    };

    return (
        <div
            onKeyDownCapture={stopKeyEvent}
            onKeyUpCapture={stopKeyEvent}
            onKeyPressCapture={stopKeyEvent}
        >
            <input
                ref={inputRef}
                list={listId}
                className="ag-input-field-input ag-text-field-input"
                value={val}
                placeholder={props.initialValue ?? ""}
                onChange={(e) => setVal(e.target.value)}
                onKeyDown={onKeyDown}
                autoComplete="off"
            />
            <datalist id={listId}>
                {(props.values ?? []).map((opt) => (
                    <option key={opt} value={opt} />
                ))}
            </datalist>
        </div>
    );
});

export default TextSuggestEditor;
